import { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';

interface UseEChartsOptions {
  renderer?: 'canvas' | 'svg';
  autoResize?: boolean;
  devicePixelRatio?: number;
}

const useECharts = (
  option: echarts.EChartsOption,
  deps: any[] = [],
  config: UseEChartsOptions = {}
) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartInstance, setChartInstance] = useState<echarts.ECharts | null>(
    null
  );

  const {
    renderer = 'canvas',
    autoResize = true,
    devicePixelRatio = window.devicePixelRatio,
  } = config;

  // Initialize chart
  useEffect(() => {
    if (!chartRef.current) return;

    // Initialize chart if not already created
    if (!chartInstance) {
      const newChart = echarts.init(chartRef.current, undefined, {
        renderer,
        devicePixelRatio,
      });

      setChartInstance(newChart);
    }

    // Clean up function
    return () => {
      // Don't dispose here as we're keeping the instance
    };
  }, [chartInstance, renderer, devicePixelRatio]);

  // Update options when dependencies change
  useEffect(() => {
    if (!chartInstance) return;

    // Apply options with animation
    chartInstance.setOption(option as any, { notMerge: false });
  }, [chartInstance, option, ...deps]);

  // Handle resizing
  useEffect(() => {
    if (!chartInstance || !autoResize) return;

    const resizeObserver = new ResizeObserver(() => {
      chartInstance.resize();
    });

    if (chartRef.current) {
      resizeObserver.observe(chartRef.current);
    }

    const handleWindowResize = () => {
      chartInstance.resize();
    };

    window.addEventListener('resize', handleWindowResize);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [chartInstance, autoResize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (chartInstance) {
        chartInstance.dispose();
      }
    };
  }, []);

  return { chartRef, chartInstance };
};

export default useECharts;
